import { apiFetch, useBackendAuth } from './apiClient';
import type { AppSettings } from '../settings/types';

export type WizzardChatMessage = { role: 'user' | 'assistant'; content: string };

export type WizzardStatus = {
  configured: boolean;
  enabled: boolean;
  model: string;
};

export type ReminderTone = 'friendly' | 'firm' | 'final';

export type ReminderDraft = { subject: string; message: string };

async function localGrokChat(apiKey: string, model: string, messages: WizzardChatMessage[]): Promise<string> {
  const res = await fetch('https://api.x.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey.trim()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ model, messages, temperature: 0.6 }),
  });
  if (!res.ok) {
    throw new Error(`Grok API error (${res.status})`);
  }
  const data = (await res.json()) as { choices?: Array<{ message?: { content?: string } }> };
  const content = data.choices?.[0]?.message?.content?.trim();
  if (!content) {
    throw new Error('Empty response from Grok');
  }
  return content;
}

export async function fetchWizzardStatus(): Promise<WizzardStatus> {
  if (useBackendAuth) {
    return apiFetch<WizzardStatus>('/api/wizzard/status');
  }
  return { configured: false, enabled: false, model: 'grok-3-mini' };
}

export async function testWizzardConnection(
  settings: AppSettings,
  isSecretConfigured: (p: string) => boolean
): Promise<{ ok: boolean; label?: string; message?: string }> {
  const w = settings.integrations.wizzard;
  const key = w.apiKey.trim();
  const hasKey = key || (useBackendAuth && isSecretConfigured('integrations.wizzard.apiKey'));
  if (!hasKey && !key) {
    return { ok: false, message: 'API key required — paste your Grok key and Save or Test' };
  }
  if (useBackendAuth) {
    try {
      const r = await apiFetch<{ ok: boolean; label: string }>('/api/wizzard/test', {
        method: 'POST',
        body: JSON.stringify({ apiKey: key || undefined, model: w.model }),
      });
      return { ok: true, label: r.label };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : 'Test failed' };
    }
  }
  try {
    await localGrokChat(key, settings.integrations.wizzard.model || 'grok-3-mini', [{ role: 'user', content: 'Reply OK' }]);
    return { ok: true, label: 'Grok connected' };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Test failed' };
  }
}

export async function wizzardChat(
  messages: WizzardChatMessage[],
  pageContext?: string
): Promise<string> {
  if (useBackendAuth) {
    const r = await apiFetch<{ reply: string }>('/api/wizzard/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, pageContext }),
    });
    return r.reply;
  }
  throw new Error('Wizzard chat requires the server backend — enable REACT_APP_USE_BACKEND');
}

export async function wizzardGenerateReminder(opts: {
  invoiceIds: string[];
  tone?: ReminderTone;
  instructions?: string;
}): Promise<ReminderDraft> {
  if (useBackendAuth) {
    return apiFetch<ReminderDraft>('/api/wizzard/generate-reminder', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
  }
  throw new Error('Wizzard reminders require the server backend');
}

export function isWizzardConfigured(settings: AppSettings, isSecretConfigured: (p: string) => boolean): boolean {
  if (!settings.integrations.wizzard.enabled) {
    return false;
  }
  if (useBackendAuth) {
    return isSecretConfigured('integrations.wizzard.apiKey');
  }
  return Boolean(settings.integrations.wizzard.apiKey.trim());
}
